import { TListResponse } from 'src/common/types';
import { TTableSort, TTableSortCols } from 'src/components/table/thead/col';

import { adaptProviderSourceFromResponse, TProviderSource, TProviderSourceResponse } from './provider-source.type';
import { adaptBrandSourceFromResponse, TBrandSource, TBrandSourceResponse } from './brand-source.type';
import { adaptUserMetaFromResponse, TUserMeta, TUserMetaResponse } from './user-meta.type';


export const ProductCartListSortCol = {
	NAME: 'name',
	SERIAL_NUMBER: 'serial_number',
	BRAND: 'brand',
	PROVIDER: 'provider',
	PRICE: 'price',
	STORAGE_NAME: 'storage_name',
	AMOUNT: 'amount',
	CREATED_AT: 'created_at',
};

export type TProductCartListSortCol = TTableSortCols<typeof ProductCartListSortCol>;

export type TProductCartListQuery = {
	limit: number;
	offset: number;
	search?: string;
	sort?: TTableSort;
	sortCol?: TProductCartListSortCol;
	userNickname?: string;
};

export type TProductCartListQueryRequest = {
	limit?: number;
	offset?: number;
	search?: string;
	sort?: string;
	sort_col?: string;
	user_nickname?: string;
};

export function adaptProductCartListQueryToRequest(data?: TProductCartListQuery): TProductCartListQueryRequest {
	if (!data) {
		return {};
	}

	const {
		sortCol,
		userNickname,
		...restFields
	} = data;

	return {
		sort_col: sortCol,
		user_nickname: userNickname,
		...restFields
	};
}

export type TProductCart = {
  id: number;
  productId: string | number;
  serialNumber: string;
  name: string;
  storageId: string;
  storageName: string;
  amount: number;
  amountType: string;
  amountMultiplicity: number;
  price: number;
  isReturnable: boolean;
  isReturnableText: string;
  provider: TProviderSource;
  brand: TBrandSource;
  orderAmount: number;
	createdAt: string;
	createdBy: TUserMeta;
};

export type TProductCartResponse = {
  id: number;
  product_id: string | number;
  serial_number: string;
  name: string;
  storage_id: string;
  storage_name: string;
  amount: number;
  amount_type: string;
  amount_multiplicity: number;
  price: number;
  is_returnable: boolean;
	provider: TProviderSourceResponse;
  brand: TBrandSourceResponse;
  order_amount: number;
	created_at: string;
	created_by: TUserMetaResponse;
};

export type TProductCartListResponse = TListResponse<TProductCartResponse>;

export type TProductCartList = TListResponse<TProductCart>;

export type TProductCartPayloadRequest = {
  userNickname: string;
  productId: string | number;
  serialNumber: string;
  name: string;
  storageId: string;
  storageName: string;
  amount: number;
  amountType: string;
  amountMultiplicity: number;
  price: number;
  isReturnable: boolean;
  providerId: number;
  brandId: number;
  orderAmount: number;
};

export type TProductCartRequest = {
  user_nickname: string;
  product_id: string | number;
  serial_number: string;
  name: string;
  storage_id: string;
  storage_name: string;
  amount: number;
  amount_type: string;
  amount_multiplicity: number;
  price: number;
  is_returnable: boolean;
	provider_id: number;
  brand_id: number;
  order_amount: number;
};

export function adaptProductCartFromListResponse(data: TProductCartListResponse): TProductCartList {
	const {
		items: itemsRaw = [],
		...restFields
	} = data;

	const items = itemsRaw.map(item => adaptProductCartFromResponse(item));

	return {
		items,
		...restFields
	};
}

export function adaptProductCartFromResponse(data: TProductCartResponse): TProductCart {
	const {
		id,
		product_id: productId,
		serial_number: serialNumber,
		name = '',
		storage_id: storageId,
		storage_name: storageName,
		amount,
		amount_type: amountType,
		amount_multiplicity: amountMultiplicity,
		price,
		is_returnable: isReturnable = false,
		provider: providerRaw,
		brand: brandRaw,
		order_amount: orderAmount,
		created_by: createdByRaw,
		created_at: createdAt,
	} = data;

	const createdBy = adaptUserMetaFromResponse(createdByRaw);
	const provider = adaptProviderSourceFromResponse(providerRaw);
	const brand = adaptBrandSourceFromResponse(brandRaw);

	const isReturnableText = isReturnable ? 'Да' : 'Нет';

	return {
		id,
		productId,
		serialNumber,
		name,
		storageId,
		storageName,
		amount,
		amountType,
		amountMultiplicity,
		price,
		isReturnable,
		isReturnableText,
		provider,
		brand,
		orderAmount,
		createdBy,
		createdAt,
	};
}

export function adaptProductCartToRequest(data: TProductCartPayloadRequest): TProductCartRequest {
	const {
		userNickname,
		productId,
		serialNumber,
		name,
		storageId,
		storageName,
		amount,
		amountType,
		amountMultiplicity,
		price,
		isReturnable,
		providerId,
		brandId,
		orderAmount,
	} = data;

	return {
		user_nickname: userNickname,
		product_id: productId,
		serial_number: serialNumber,
		name: name,
		storage_id: storageId,
		storage_name: storageName,
		amount: amount,
		amount_type: amountType,
		amount_multiplicity: amountMultiplicity,
		price: price,
		is_returnable: isReturnable,
		provider_id: providerId,
		brand_id: brandId,
		order_amount: orderAmount,
	};
}
